/**
 * Parallel data loading for the dashboard page.
 *
 * Mirrors the four `get_*` calls at the top of pages/3_dashboard.py:
 *  - balance row (balance metrics)
 *  - current-week row (the Raw x Variety + Streak = Earned formula)
 *  - earned-spent rows (12-week diverging bar + running balance line)
 * Streaks and week-categories are loaded lazily by the dialogs themselves
 * (see useStreaks / useWeekCategories) so the page paints without them.
 *
 * CRITICAL: every query key starts with ['dashboard', ...]; the workout save
 * mutation invalidates the ['dashboard'] prefix on every write.
 */
import { useQueries, useQuery } from "@tanstack/react-query";

import { getBalance, getCurrentWeek, getEarnedSpent, getStreaks, getWeekCategories } from "@/api/dashboard";
import type { BalanceRow, CurrentWeekRow, EarnedSpentRow, StreakRow, WeekCategoryRow } from "@/api/responses";

export interface DashboardData {
  isLoading: boolean;
  isError: boolean;
  balance: BalanceRow | null;
  currentWeek: CurrentWeekRow | null;
  earnedSpent: EarnedSpentRow[];
}

export function useDashboardData(): DashboardData {
  const results = useQueries({
    queries: [
      { queryKey: ["dashboard", "balance"], queryFn: getBalance },
      { queryKey: ["dashboard", "current-week"], queryFn: getCurrentWeek },
      { queryKey: ["dashboard", "earned-spent"], queryFn: getEarnedSpent },
    ],
  });

  const [balanceQ, weekQ, earnedSpentQ] = results;

  return {
    isLoading: results.some((r) => r.isLoading),
    isError: results.some((r) => r.isError),
    balance: (balanceQ.data ?? null) as BalanceRow | null,
    currentWeek: (weekQ.data ?? null) as CurrentWeekRow | null,
    earnedSpent: (earnedSpentQ.data ?? []) as EarnedSpentRow[],
  };
}

/** Streak rows for the StreakDialog — fetched lazily when the dialog opens. */
export function useStreaks(enabled: boolean) {
  return useQuery<StreakRow[]>({
    queryKey: ["dashboard", "streaks"],
    queryFn: getStreaks,
    enabled,
  });
}

/** Per-day category breakdown for the VarietyDialog — fetched lazily on open. */
export function useWeekCategories(enabled: boolean) {
  return useQuery<WeekCategoryRow[]>({
    queryKey: ["dashboard", "week-categories"],
    queryFn: getWeekCategories,
    enabled,
  });
}
